// React imports
import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

// Other imports
import axios from 'axios';
import _ from 'lodash';
import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';

// Local imports
import { convertMonadaCsv } from '../dashboard/rates/utils.js';
import { sectionedForm, chargeSection, isSectioned, formFields, moveField, SECTION_ORDER } from '../dashboard/rates/chargeGlossary.js';
import { allProductTypes } from '../ProductTypes.js';
import { Pill } from './ElariomParts.js';
import { useOlderRates, restoreOlderRates, OlderRatesPanel, olderRatesSentence } from './ElariomOlderRates.js';

// Review & edit, then Publish, for one agent run on a dropped file.
//
// The reviewer sees the rates as the importer CSV the agent produced, can correct any cell
// and leave rows out. Every change is checked again with the importer's own code, so a row
// the importer would reject is marked before publishing. Publish creates the rate form and the
// ticked rates in the workspace this page belongs to, through the regular rates API, and
// records the rate ids so the batch can be unpublished in one step.

const HIDDEN_COLUMNS = ['transportationMethod', 'type', 'form', 'supplierEmail'];
const REVIEW_FORM_KEY = '__review__';
const CHUNK = 100;
const CHARGE_TYPES = [
    { value: 'per-unit-type', label: 'per container' },
    { value: 'per-unit', label: 'per kg (air) or per cbm/ton (LCL)' },
    { value: 'per-unit-weight', label: 'per kg of gross weight' },
    { value: 'flat', label: 'once per shipment' },
    { value: 'string', label: 'as text, not a price' },
    { value: 'date', label: 'as a date' },
];

function cellKey(row, col) {
    return `${row}:${col}`;
}

function ElariomReview({ org, accesstoken, drop, runSummary, run, onClose, onPublished }) {
    const theme = useTheme();
    const { unlocode, seaPorts, onOrgUpdated } = useOutletContext();
    const parsed = useMemo(() => Papa.parse(run.csv || '', { header: false, skipEmptyLines: true }).data, [run.csv]);
    const header = parsed[0] || [];
    const original = useMemo(() => parsed.slice(1), [parsed]);

    const [ edits, setEdits ] = useState({});
    const [ omitted, setOmitted ] = useState(() => new Set());
    const [ editing, setEditing ] = useState(null);
    const [ check, setCheck ] = useState(null);
    const [ confirm, setConfirm ] = useState(false);
    const [ progress, setProgress ] = useState(null);
    const [ error, setError ] = useState(null);
    const [ createdForm, setCreatedForm ] = useState(null);
    // A form made from a file's columns has guessed charge types; the reviewer can change them.
    const editableForm = run.formSource === 'new';
    const [ formDraft, setFormDraft ] = useState(run.form);
    // What the reviewer changed on the drafted form: charge types, and charges moved to another
    // section (kept with the run's corrections, so the charge glossary can learn from them).
    const originalFields = formFields(run.form);
    const sectionOf = (form, id) => (form?.sections || []).find(sec => (sec.fields || []).some(f => f.id === id))?.title;
    const typeChanges = editableForm ? [
        ...formFields(formDraft)
            .map(f => ({ f, was: originalFields.find(o => o.id === f.id)?.type }))
            .filter(({ f, was }) => f.type !== was)
            .map(({ f, was }) => ({ row: null, column: `charge type: ${f.title}`, from: was, to: f.type })),
        ...formFields(formDraft)
            .map(f => ({ f, was: sectionOf(run.form, f.id), now: sectionOf(formDraft, f.id) }))
            .filter(({ was, now }) => was && now && was !== now)
            .map(({ f, was, now }) => ({ row: null, column: `section: ${f.title}`, from: was, to: now })),
    ] : [];
    const publication = runSummary.publication?.status === 'published' ? runSummary.publication : null;

    const rows = useMemo(() => original.map((r, i) => r.map((v, c) => (edits[cellKey(i, c)] !== undefined ? edits[cellKey(i, c)] : v))), [original, edits]);

    // Columns that aren't a charge on an existing form would be dropped by the importer. Each
    // one needs a decision before publishing: map it to a charge, add it to the form, or leave
    // it out. A column with no values has nothing to lose and is left out without asking.
    const unmatched = run.formSource === 'existing' ? (run.unmatchedColumns || []) : [];
    const [ columnDecisions, setColumnDecisions ] = useState(() => Object.fromEntries(unmatched.filter(u => !u.filledRows).map(u => [u.column, { action: 'leave' }])));
    const undecided = unmatched.filter(u => !columnDecisions[u.column]);
    const fieldTitles = (run.form?.sections || []).flatMap(sec => sec.fields || []).map(f => f.title);
    // The form the rates are checked and published against: the run's form plus any column the
    // reviewer chose to add to it.
    const formForRates = useMemo(() => {
        const added = unmatched.filter(u => columnDecisions[u.column]?.action === 'add')
            .map(u => ({ id: `added-${u.column}`, title: u.column, type: columnDecisions[u.column].type || u.suggestedType }));
        if (!added.length) return formDraft;
        return { ...formDraft, sections: formDraft.sections.map((sec, i) => (i === 0 ? { ...sec, fields: [...sec.fields, ...added] } : sec)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDraft, columnDecisions]);

    // The CSV with the column decisions applied: a mapped column takes its charge's title (and
    // fills it where the file's own column for that charge is empty); a column left out is dropped.
    const csvFor = (formTitle, keep) => {
        const decision = c => columnDecisions[c];
        const outHeader = [];
        const pick = [];
        header.forEach((h, c) => {
            const d = decision(h);
            if (d?.action === 'leave') return;
            if (d?.action === 'map' && header.includes(d.to)) return;
            outHeader.push(d?.action === 'map' ? d.to : h);
            pick.push(c);
        });
        const mergedInto = Object.fromEntries(header.map((h, c) => [h, c]).filter(([h]) => decision(h)?.action === 'map' && header.includes(decision(h).to)).map(([h, c]) => [decision(h).to, c]));
        return Papa.unparse({
            fields: outHeader,
            data: rows.filter((r, i) => keep(i)).map(r => pick.map(c => {
                if (header[c] === 'form') return formTitle;
                const extra = mergedInto[header[c]];
                return (r[c] === '' || r[c] === undefined) && extra !== undefined ? r[extra] : r[c];
            })),
        }, { quotes: true });
    };

    // Re-check every row with the importer whenever a cell changes.
    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            const result = await convertMonadaCsv({
                data: csvFor(formForRates.title, () => true),
                org: { ...org, forms: { ...(org.forms || {}), [REVIEW_FORM_KEY]: formForRates } },
                unlocode, seaPorts,
            });
            const reasons = {};
            result.failed.forEach(f => { reasons[f.index] = 'The importer can\'t read this row: a place code, the product or the form could not be matched.'; });
            // The importer accepts any product name, but rates are saved only with a Monada
            // product, so an unknown one would fail at publish.
            result.successful.forEach(({ rate, index }) => {
                const known = allProductTypes(rate.transportationMethod).some(p => p.name === rate.product?.type);
                if (!known) reasons[index] = `"${rate.product?.type}" isn't a Monada product for ${rate.transportationMethod}. Correct the product cell, e.g. "20' Dry".`;
            });
            if (!cancelled) setCheck({ failed: new Set(Object.keys(reasons).map(Number)), reasons, converted: result.successful });
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, unlocode, seaPorts, formForRates, columnDecisions]);

    const failed = check?.failed || new Set();
    const publishable = rows.map((r, i) => i).filter(i => !omitted.has(i) && !failed.has(i));

    // Older versions of these rates already in the workspace (see ElariomOlderRates.js).
    const ratesToPublish = useMemo(() => {
        const keep = new Set(publishable);
        return (check?.converted || []).filter(c => keep.has(c.index)).map(c => c.rate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [check, omitted]);
    const older = useOlderRates({ org, accesstoken, rates: ratesToPublish, ownIds: runSummary.publication?.rateIds });
    const { replaced } = older;
    const corrections = Object.entries(edits)
        .filter(([key, value]) => { const [r, c] = key.split(':').map(Number); return original[r][c] !== value; })
        .map(([key, value]) => { const [r, c] = key.split(':').map(Number); return { row: r + 1, column: header[c], from: original[r][c], to: value }; });
    const columnChoices = unmatched.filter(u => columnDecisions[u.column]).map(u => {
        const d = columnDecisions[u.column];
        return {
            column: u.column, filledRows: u.filledRows, action: d.action,
            ...(d.action === 'map' ? { to: d.to } : {}), ...(d.action === 'add' ? { type: d.type || u.suggestedType } : {}),
        };
    });

    const publish = async () => {
        setError(null);
        try {
            setProgress({ label: 'Creating the rate form…', value: 5 });
            const FORM_KEYS = ['type', 'title', 'description', 'transportationMethods', 'incoterms', 'preface', 'routes', 'displayCurrency', 'displayCurrencySource', 'displayCurrencyEnabled', 'sections', 'createdOn', 'createdBy'];
            const clean = f => ({ ..._.pick(f, FORM_KEYS), sections: f.sections.map(sec => ({ ...sec, fields: sec.fields.map(x => _.omit(x, ['guessed'])) })) });
            const added = columnChoices.filter(c => c.action === 'add');
            // A new form is saved as reviewed: drafted in Origin / Freight / Destination sections
            // by the charge glossary, with any section the reviewer changed. A form drafted before
            // the glossary (one "Charges" section) is sectioned here. An existing form is used as it is.
            let form = run.formSource === 'existing' ? clean(run.form) : isSectioned(formDraft) ? clean(formDraft) : sectionedForm(clean(formDraft));
            if (run.formSource === 'existing' && added.length) {
                // Add the chosen columns to the workspace's form as new charges. Rates already on
                // the form keep their sections; only rates published from now on carry them.
                setProgress({ label: `Adding ${added.length} charge${added.length === 1 ? '' : 's'} to the form "${run.form.title}"…`, value: 5 });
                // Each goes to the section of its kind when the form has Origin / Freight /
                // Destination sections, otherwise to the first section.
                const sectionFor = a => {
                    const want = { o: 'origin', f: 'freight', d: 'destination' }[a.type === 'string' ? 'f' : chargeSection(a.column)];
                    const i = form.sections.findIndex(sec => String(sec.title || '').trim().toLowerCase() === want);
                    return i === -1 ? 0 : i;
                };
                form = { ...form, sections: form.sections.map((sec, i) => ({ ...sec, fields: [...sec.fields, ...added.filter(a => sectionFor(a) === i).map(a => ({ id: uuidv4(), title: a.column, type: a.type }))] })) };
                await axios.put(`/api/organizations/${org.id}/forms/${run.formId}?accesstoken=${accesstoken}`, form);
            }
            // An existing form is used as it is. A retry after a failed publish reuses the form it
            // already created, even after a reload.
            let formId = (run.formSource === 'existing' && run.formId) || createdForm || _.findKey(org.forms || {}, f => f.title === form.title && !f.deleted);
            if (!formId) {
                ({ data: formId } = await axios.post(`/api/organizations/${org.id}/forms?accesstoken=${accesstoken}`, form));
                setCreatedForm(formId);
            }
            const orgWithForm = { ...org, forms: { ...(org.forms || {}), [formId]: { ...form, id: formId } } };

            setProgress({ label: 'Reading the reviewed rates…', value: 15 });
            const keep = new Set(publishable);
            const converted = await convertMonadaCsv({ data: csvFor(form.title, i => keep.has(i)), org: orgWithForm, unlocode, seaPorts });
            const source = drop.files.find(f => f.name === runSummary.fileName)?.url || null;
            const rates = converted.successful.map(s => ({ ...s.rate, attributes: { ...(s.rate.attributes || {}), ...(source ? { source } : {}) } }));

            const rateIds = [];
            const failedOnSave = [];
            for (let i = 0; i < rates.length; i += CHUNK) {
                setProgress({ label: `Publishing rates ${i + 1}–${Math.min(i + CHUNK, rates.length)} of ${rates.length}…`, value: 20 + Math.round((i / rates.length) * 70) });
                const { data } = await axios.post(`/api/rates/bulk?accesstoken=${accesstoken}&organizationId=${org.id}`, { rates: rates.slice(i, i + CHUNK) });
                rateIds.push(...(data.successful || []).map(s => s.rate?.id || s.id).filter(Boolean));
                failedOnSave.push(...(data.failed || []));
            }

            let replacedRecord = null;
            if (replaced.length) {
                setProgress({ label: older.action === 'end' ? `Ending ${replaced.length} older rates…` : older.action === 'delete' ? `Deleting ${replaced.length} older rates…` : 'Recording the older rates…', value: 92 });
                replacedRecord = await older.apply();
            }

            setProgress({ label: 'Recording what was published…', value: 95 });
            const { data: record } = await axios.put(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs/${runSummary.id}/publication?accesstoken=${accesstoken}`, {
                status: 'published', formId, formTitle: form.title, rateIds, corrections: [...corrections, ...typeChanges], columnDecisions: columnChoices, replaced: replacedRecord,
                omittedRows: [...omitted].map(i => i + 1),
                failedRows: [...failed].map(i => i + 1).concat(failedOnSave.map(f => `save: ${f.error || 'failed'}`)),
            });
            onOrgUpdated?.(orgWithForm);
            setProgress(null);
            setConfirm(false);
            onPublished(record);
        } catch (e) {
            setProgress(null);
            setError(e.response?.data?.detail || e.message);
        }
    };

    const unpublish = async () => {
        setError(null);
        try {
            const ids = publication.rateIds || [];
            for (let i = 0; i < ids.length; i += CHUNK) {
                setProgress({ label: `Removing rates ${i + 1}–${Math.min(i + CHUNK, ids.length)} of ${ids.length}…`, value: Math.round((i / Math.max(ids.length, 1)) * 90) });
                await axios.post(`/api/rates/admin-delete?accesstoken=${accesstoken}&organizationId=${org.id}`, { rates: ids.slice(i, i + CHUNK) });
            }
            if (publication.replaced) {
                setProgress({ label: 'Putting the older rates back as they were…', value: 95 });
                await restoreOlderRates({ org, accesstoken, record: publication.replaced });
            }
            const { data: record } = await axios.put(`/api/admin/elariom/${org.id}/drops/${drop.id}/runs/${runSummary.id}/publication?accesstoken=${accesstoken}`, { status: 'unpublished' });
            setProgress(null);
            setConfirm(false);
            onPublished(record);
        } catch (e) {
            setProgress(null);
            setError(e.response?.data?.detail || e.message);
        }
    };

    // An existing form can have many more charges than this file fills. A rate gets only the
    // charges the file has a column for (convertMonadaCsv), so only those are shown; a column the
    // reviewer maps to a charge counts as that charge.
    const inFile = new Set(header.map(h => (columnDecisions[h]?.action === 'map' ? columnDecisions[h].to : h)));
    const shownForm = run.formSource === 'existing'
        ? { ...run.form, sections: (run.form?.sections || []).map(sec => ({ ...sec, fields: (sec.fields || []).filter(f => inFile.has(f.title)) })).filter(sec => sec.fields.length) }
        : run.form;
    const notInFile = run.formSource === 'existing' ? fieldTitles.filter(t => !inFile.has(t)) : [];
    const visible = header.map((h, c) => c).filter(c => !HIDDEN_COLUMNS.includes(header[c]));
    const workspace = org.title || org.alias;
    const th = { position: 'sticky', top: 0, zIndex: 1, background: theme.palette.panel1, fontSize: '11px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', padding: '6px 8px', borderBottom: `1px solid ${theme.palette.border1}`, color: theme.palette.text.secondary };
    const td = { fontSize: '12px', padding: '3px 8px', borderBottom: `1px solid ${alpha(theme.palette.text.secondary, 0.12)}`, whiteSpace: 'nowrap', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' };

    return (
        <Dialog open onClose={() => !progress && onClose()} fullScreen>
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Review rates · {run.supplier?.name || runSummary.supplier || 'supplier'}</Typography>
                    <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                        {runSummary.fileName} · {run.classification?.category} · publishes to <b>{workspace}</b>
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Pill color={theme.palette.text.secondary}>{rows.length} rows</Pill>
                    <Pill color={theme.palette.primary.main}>{publishable.length} to publish</Pill>
                    {omitted.size > 0 && <Pill color={theme.palette.text.secondary}>{omitted.size} left out</Pill>}
                    {failed.size > 0 && <Pill color={theme.palette.warning.main}>{failed.size} can't be published</Pill>}
                    {corrections.length > 0 && <Pill color={theme.palette.info.main}>{corrections.length} edit{corrections.length === 1 ? '' : 's'}</Pill>}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {publication && (
                    <Alert severity='success' sx={{ fontSize: '13px' }}>
                        Published {publication.rateCount} rates to {workspace} on {dayjs(publication.at).format('MMM D, HH:mm')} by {publication.by}, with the rate form "{publication.formTitle}".
                        {publication.corrections?.length ? ` ${publication.corrections.length} cell${publication.corrections.length === 1 ? ' was' : 's were'} corrected.` : ''}
                    </Alert>
                )}
                {runSummary.publication?.status === 'unpublished' && (
                    <Alert severity='info' sx={{ fontSize: '13px' }}>
                        Unpublished on {dayjs(runSummary.publication.unpublishedAt).format('MMM D, HH:mm')}: its {runSummary.publication.rateCount} rates were removed from {workspace}. The rate form "{runSummary.publication.formTitle}" was kept.
                    </Alert>
                )}
                {error && <Alert severity='error' sx={{ fontSize: '13px' }}>{error}</Alert>}
                {run.recipe && !publication && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        {run.recipeSource === 'saved'
                            ? 'These rates were read with the recipe saved for this file layout: no model read them.'
                            : 'These rates were read with a recipe the agent wrote for this table. When you publish, the recipe is saved, and the next file in the same layout is read without a model.'}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, marginRight: '4px' }}>
                        {run.formSource === 'existing'
                            ? `The rates will use the rate form "${run.form.title}", which already exists in ${workspace}. The charges in this file:`
                            : editableForm
                                ? `A new rate form "${formDraft.title}" will be created from the file's columns, with its charges in Origin, Freight and Destination sections from the charge glossary. How each charge is priced was guessed (dashed outline); check each charge's price type and section, and change them if needed:`
                                : 'Charges on the rate form:'}
                    </Typography>
                    {(editableForm && !publication ? formDraft : shownForm)?.sections?.map(sec => (
                        <Box key={sec.title} sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: theme.palette.text.secondary, minWidth: '92px' }}>{sec.title}</Typography>
                            {editableForm && !publication
                                ? (sec.fields || []).map(f => {
                                    const was = originalFields.find(o => o.id === f.id);
                                    const moved = sectionOf(run.form, f.id) !== sec.title;
                                    return (
                                        <Box key={f.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', border: `1px ${f.guessed && f.type === was?.type && !moved ? 'dashed' : 'solid'} ${theme.palette.border1}`, borderRadius: '999px', padding: '1px 4px 1px 10px', fontSize: '11px' }}>
                                            {f.title}
                                            <select
                                                aria-label={`Charge type for ${f.title}`}
                                                value={f.type}
                                                onChange={e => { const type = e.target.value; setFormDraft(d => ({ ...d, sections: d.sections.map(x => ({ ...x, fields: x.fields.map(y => (y.id === f.id ? { ...y, type } : y)) })) })); }}
                                                style={{ font: 'inherit', fontSize: '11px', border: 'none', background: 'transparent', color: theme.palette.text.secondary }}
                                            >
                                                {CHARGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                            <select
                                                aria-label={`Section for ${f.title}`}
                                                value={sec.title}
                                                onChange={e => { const to = e.target.value; setFormDraft(d => moveField(d, f.id, to)); }}
                                                style={{ font: 'inherit', fontSize: '11px', border: 'none', background: 'transparent', color: theme.palette.text.secondary }}
                                            >
                                                {SECTION_ORDER.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </Box>
                                    );
                                })
                                : (sec.fields || []).map(f => <Pill key={f.id || f.title} color={theme.palette.text.secondary}>{f.title} · {f.type}</Pill>)}
                        </Box>
                    ))}
                </Box>
                {notInFile.length > 0 && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        {notInFile.length === 1
                            ? `The form's charge "${notInFile[0]}" isn't in this file, so these rates won't have it.`
                            : `${notInFile.length} other charges on the form aren't in this file, so these rates won't have them: ${notInFile.join(', ')}.`}
                    </Typography>
                )}
                {!publication && <OlderRatesPanel older={older} workspace={workspace} />}
                {publication?.replaced && publication.replaced.count > 0 && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        Older versions of these rates: {publication.replaced.count} {publication.replaced.action === 'end' ? 'were ended the day before the new rates start' : publication.replaced.action === 'delete' ? 'were deleted' : 'were kept as they were'}{publication.replaced.action !== 'keep' ? '. Unpublishing puts them back.' : '.'}
                    </Typography>
                )}
                {unmatched.length > 0 && !publication && (
                    <Box sx={{ border: `1px solid ${undecided.length ? theme.palette.warning.main : theme.palette.border1}`, borderRadius: '8px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Box>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                                {unmatched.length === 1
                                    ? `This file has a column that doesn't match any charge on the rate form "${run.form.title}"`
                                    : `This file has ${unmatched.length} columns that don't match any charge on the rate form "${run.form.title}"`}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, marginTop: '2px', maxWidth: '760px' }}>
                                Monada saves a column with the rates only when the rate form has a charge with the same name. Choose what to do with {unmatched.length === 1 ? 'this column' : 'each column'} below. You can publish once every column has a choice.
                            </Typography>
                        </Box>
                        {unmatched.map(u => {
                            const d = columnDecisions[u.column];
                            const set = value => setColumnDecisions(prev => ({ ...prev, [u.column]: value }));
                            return (
                                <Box key={u.column} sx={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '8px', borderTop: `1px solid ${theme.palette.border1}` }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', fontSize: '13px' }}>
                                        <Box sx={{ minWidth: '240px' }}>
                                            <b>{u.column}</b>
                                            <span style={{ color: theme.palette.text.secondary }}> — has values in {u.filledRows} row{u.filledRows === 1 ? '' : 's'}</span>
                                        </Box>
                                        <select
                                            aria-label={`What to do with the column ${u.column}`}
                                            value={d ? (d.action === 'map' ? `map:${d.to}` : d.action) : ''}
                                            onChange={e => {
                                                const v = e.target.value;
                                                if (!v) set(undefined);
                                                else if (v.startsWith('map:')) set({ action: 'map', to: v.slice(4) });
                                                else if (v === 'add') set({ action: 'add', type: u.suggestedType });
                                                else set({ action: 'leave' });
                                            }}
                                            style={{ font: 'inherit', fontSize: '13px', padding: '4px 6px', maxWidth: '100%' }}
                                        >
                                            <option value=''>Choose what to do with this column…</option>
                                            <option value='add'>Add "{u.column}" to the rate form as a new charge</option>
                                            {u.suggestion && <option value={`map:${u.suggestion}`}>Save it in the existing charge "{u.suggestion}" (suggested)</option>}
                                            <optgroup label='Save it in an existing charge on the form:'>
                                                {fieldTitles.filter(t => t !== u.suggestion).map(t => <option key={t} value={`map:${t}`}>{t}</option>)}
                                            </optgroup>
                                            <option value='leave'>Don't import this column</option>
                                        </select>
                                    </Box>
                                    {d?.action === 'add' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px', color: theme.palette.text.secondary }}>
                                            "{u.column}" will be added to the rate form "{run.form.title}" as a charge priced
                                            <select
                                                aria-label={`How the charge ${u.column} is priced`}
                                                value={d.type || u.suggestedType}
                                                onChange={e => set({ action: 'add', type: e.target.value })}
                                                style={{ font: 'inherit', fontSize: '13px', padding: '3px 6px' }}
                                            >
                                                {CHARGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                            . Rates you publish from now on will include it; rates already on the form don't change.
                                        </Box>
                                    )}
                                    {d?.action === 'map' && (
                                        <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                                            The values in this column will be saved in the charge "{d.to}".
                                        </Typography>
                                    )}
                                    {d?.action === 'leave' && u.filledRows > 0 && (
                                        <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                                            The values in this column won't be saved with the rates. Your choice is recorded with the publish, so it's clear the column was left out on purpose.
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: undecided.length ? theme.palette.warning.main : theme.palette.success.main }}>
                            {undecided.length
                                ? `Choose what to do with ${undecided.length === 1 ? 'the column above' : `${undecided.length} more columns`} to be able to publish.`
                                : 'Every column has a choice. You can publish.'}
                        </Typography>
                    </Box>
                )}
                {publication?.columnDecisions?.length > 0 && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        Columns that didn't match the form: {publication.columnDecisions.map(c => `"${c.column}" ${c.action === 'map' ? `was saved in the charge "${c.to}"` : c.action === 'add' ? 'was added to the form as a new charge' : 'was not imported'}`).join('; ')}.
                    </Typography>
                )}
                {!publication && (
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        To correct a value, click its cell and type. To skip a rate, untick its row. Rows that can't be published are shaded and can't be ticked; point at the box to see why.
                    </Typography>
                )}
                <Box sx={{ flexGrow: 1, overflow: 'auto', border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: theme.palette.panel1 }}>
                    <Box component='table' sx={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                <Box component='th' sx={{ ...th, left: 0, zIndex: 2, width: '36px' }} />
                                <Box component='th' sx={th}>#</Box>
                                {visible.map(c => <Box component='th' key={c} sx={th}>{header[c]}</Box>)}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                const isFailed = failed.has(i);
                                const isOmitted = omitted.has(i);
                                return (
                                    <Box component='tr' key={i} sx={{ background: isFailed ? alpha(theme.palette.warning.main, 0.08) : 'transparent', opacity: isOmitted ? 0.45 : 1 }}>
                                        <Box component='td' sx={{ ...td, position: 'sticky', left: 0, background: theme.palette.panel1, padding: 0 }}>
                                            <Tooltip title={isFailed ? check?.reasons?.[i] || '' : ''}>
                                                <span>
                                                    <Checkbox
                                                        size='small'
                                                        disabled={!!publication || isFailed}
                                                        checked={!isOmitted && !isFailed}
                                                        onChange={() => setOmitted(prev => { const next = new Set(prev); next.has(i) ? next.delete(i) : next.add(i); return next; })}
                                                        inputProps={{ 'aria-label': `Publish row ${i + 1}` }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </Box>
                                        <Box component='td' sx={{ ...td, color: theme.palette.text.secondary }}>{i + 1}</Box>
                                        {visible.map(c => {
                                            const key = cellKey(i, c);
                                            const changed = edits[key] !== undefined && edits[key] !== original[i][c];
                                            if (editing === key) {
                                                return (
                                                    <Box component='td' key={c} sx={{ ...td, padding: '1px 4px' }}>
                                                        <input
                                                            autoFocus
                                                            defaultValue={r[c]}
                                                            aria-label={`${header[c]}, row ${i + 1}`}
                                                            onBlur={e => { const v = e.target.value; setEdits(prev => ({ ...prev, [key]: v })); setEditing(null); }}
                                                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditing(null); }}
                                                            style={{ font: 'inherit', fontSize: '12px', width: `${Math.max(8, String(r[c]).length + 2)}ch`, padding: '2px 4px' }}
                                                        />
                                                    </Box>
                                                );
                                            }
                                            return (
                                                <Box
                                                    component='td'
                                                    key={c}
                                                    title={changed ? `Was: ${original[i][c]}` : r[c]}
                                                    onClick={() => !publication && setEditing(key)}
                                                    sx={{ ...td, cursor: publication ? 'default' : 'text', background: changed ? alpha(theme.palette.info.main, 0.12) : 'transparent', '&:hover': publication ? {} : { outline: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`, outlineOffset: '-1px' } }}
                                                >
                                                    {r[c]}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                        </tbody>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '12px 24px', gap: '8px' }}>
                <Button onClick={onClose} disabled={!!progress} sx={{ textTransform: 'none' }}>Close</Button>
                {publication
                    ? <Button variant='outlined' color='error' onClick={() => setConfirm(true)} disabled={!!progress} sx={{ textTransform: 'none' }}>Unpublish {publication.rateCount} rates</Button>
                    : <Button variant='contained' color='monada-button' onClick={() => setConfirm(true)} disabled={!check || !publishable.length || undecided.length > 0 || !!progress} sx={{ textTransform: 'none' }}>
                        Publish {publishable.length} rates to {workspace}
                    </Button>}
            </DialogActions>

            <Dialog open={confirm} onClose={() => !progress && setConfirm(false)} maxWidth='xs' fullWidth>
                <DialogTitle sx={{ fontSize: '17px' }}>{publication ? `Unpublish from ${workspace}?` : `Publish to ${workspace}?`}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {publication ? (
                        <Typography sx={{ fontSize: '14px' }}>
                            This deletes the {publication.rateCount} rates published from this file. Quotes already priced with them keep their prices. The rate form is kept.
                            {publication.replaced && ['end', 'delete'].includes(publication.replaced.action) ? ` The ${publication.replaced.count} older rates this file replaced are put back as they were.` : ''}
                        </Typography>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '14px' }}>
                                {run.formSource === 'existing'
                                    ? `This creates ${publishable.length} rates in ${workspace} on its existing form "${run.form.title}".`
                                    : `This creates the rate form "${formDraft.title}" and ${publishable.length} rates in ${workspace}.`} Rate search and quoting use them right away.
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                                {olderRatesSentence(older)}
                                You can unpublish this file later from the same place, which also puts any older rates back.
                            </Typography>
                        </>
                    )}
                    {progress && (
                        <Box>
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, marginBottom: '4px' }}>{progress.label}</Typography>
                            <LinearProgress variant='determinate' value={progress.value} />
                        </Box>
                    )}
                    {error && <Alert severity='error' sx={{ fontSize: '13px' }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(false)} disabled={!!progress} sx={{ textTransform: 'none' }}>Cancel</Button>
                    {publication
                        ? <Button variant='contained' color='error' onClick={unpublish} disabled={!!progress} sx={{ textTransform: 'none' }}>Unpublish</Button>
                        : <Button variant='contained' color='monada-button' onClick={publish} disabled={!!progress} sx={{ textTransform: 'none' }}>Publish</Button>}
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}

export default ElariomReview;
